import type { AppConfig } from '@/config/AppConfig';
import { isE2ETestMode } from '../utils/e2e';
import { createAuth0Adapter } from './auth0-adapter';
import { createE2EAuthAdapter } from './e2e-adapter';
import { createNullAuthAdapter } from './null-adapter';
import { createOidcAdapter } from './oidc-adapter';
import {
  AUTH0_PROVIDER_KEY,
  findAuthProvider,
  readAuthProviders,
} from './provider-config';
import { EAuthProviderType, type IAuthAdapter } from './types';

/**
 * Inputs for {@link selectAuthAdapter}.
 */
export interface ISelectAuthAdapterParams {
  /** Resolved runtime configuration. */
  appConfig: AppConfig;
  /** Provider key resolved by the bootstrap gate, or null when none is known. */
  providerKey?: string | null;
}

/**
 * Whether this deployment has an Auth0 tenant.
 *
 * A config with no `authProviders` field predates the list and is Auth0-only;
 * a list without the `auth0` entry is a federated-only deployment.
 *
 * @param {AppConfig} appConfig - Resolved runtime configuration
 * @returns {boolean} true when Auth0 can be mounted
 */
export const hasAuth0Provider = (appConfig: AppConfig): boolean =>
  !Array.isArray(appConfig.authProviders) ||
  findAuthProvider(readAuthProviders(appConfig), AUTH0_PROVIDER_KEY) !==
    undefined;

const selectDefaultAdapter = (appConfig: AppConfig): IAuthAdapter => {
  if (hasAuth0Provider(appConfig)) return createAuth0Adapter(appConfig);

  const providers = readAuthProviders(appConfig);
  const [soleProvider] = providers;

  if (providers.length === 1 && soleProvider.type === EAuthProviderType.OIDC) {
    return createOidcAdapter(soleProvider);
  }

  // Several customer IdPs and nothing to choose between them: guessing would
  // send users to another tenant's IdP, and there is no Auth0 to fall back to.
  return createNullAuthAdapter();
};

/**
 * Picks the adapter to mount for this page load.
 *
 * With no provider key, Auth0 is mounted when the deployment has it; a
 * federated-only deployment mounts its sole provider, or the null adapter when
 * it has several.
 *
 * @param {ISelectAuthAdapterParams} params - Selection inputs
 * @param {AppConfig} params.appConfig - Resolved runtime configuration
 * @param {string | null} [params.providerKey] - Resolved provider key, if any
 * @returns {IAuthAdapter} The adapter to mount
 */
export const selectAuthAdapter = ({
  appConfig,
  providerKey,
}: ISelectAuthAdapterParams): IAuthAdapter => {
  // The E2E bypass must never sit behind provider resolution: a suite whose
  // identity comes from a stub would otherwise start depending on operator
  // config it has no way to set. `auth-provider.tsx` short-circuits on the same
  // check before calling this, so in practice this guard is belt to that brace.
  if (isE2ETestMode()) return createE2EAuthAdapter();

  if (!providerKey) return selectDefaultAdapter(appConfig);

  const provider = findAuthProvider(readAuthProviders(appConfig), providerKey);
  const isAuth0 =
    providerKey === AUTH0_PROVIDER_KEY ||
    provider?.type === EAuthProviderType.AUTH0;

  if (isAuth0) {
    return hasAuth0Provider(appConfig)
      ? createAuth0Adapter(appConfig)
      : createNullAuthAdapter();
  }

  if (provider?.type === EAuthProviderType.OIDC) {
    return createOidcAdapter(provider);
  }

  // An unknown key lands on the null adapter — logged out, with nothing loaded
  // behind it.
  return createNullAuthAdapter();
};
